/**
 * Step 5: Financial Terms
 * Configure rent, security deposit, late fees, and payment day
 */

import { useEffect } from 'react';
import type { WizardState, JurisdictionRules } from '../../../types/lease-generation';
import { RequiredMark } from '../RequiredMark';
import { LocalRuleNote } from '../../../components/LocalRuleNote';

const ALL_UTILITIES = [
  { id: 'Electricity', label: 'Electricity' },
  { id: 'Gas / Heat', label: 'Gas / Heat' },
  { id: 'Water & Sewer', label: 'Water & Sewer' },
  { id: 'Garbage / Recycling', label: 'Garbage / Recycling' },
  { id: 'Internet / Cable', label: 'Internet / Cable' },
];

type UtilityTier = 'tenantArranged' | 'includedInRent' | 'chargedBack';

const TIER_LABELS: Record<UtilityTier, string> = {
  tenantArranged: 'Tenant arranges & pays',
  includedInRent: 'Included in rent',
  chargedBack: 'Landlord pays, charged back as fee',
};

interface Step5Props {
  state: WizardState;
  updateInputs: (partial: Partial<WizardState['inputs']>) => void;
  updateParking: (partial: Partial<WizardState['parking']>) => void;
  updateUtilities: (partial: Partial<WizardState['utilities']>) => void;
}

type LateFeeType = 'none' | 'flat' | 'percentage' | 'daily';

const LATE_FEE_LABELS: Record<LateFeeType, string> = {
  none: 'No late fee',
  flat: 'Flat amount',
  percentage: 'Percentage of rent',
  daily: 'Daily fee with cap',
};

export function Step5FinancialTerms({ state, updateInputs, updateParking, updateUtilities }: Step5Props) {
  const { inputs, jurisdictionInfo, parking, utilities } = state;

  const rentAmount = inputs.rentAmount ?? '';
  const securityDeposit = inputs.securityDeposit ?? '';
  const lateFeeType: LateFeeType = inputs.lateFeeType ?? 'none';
  const lateFeeAmount = inputs.lateFeeAmount ?? '';
  const lateFeeMaxAmount = inputs.lateFeeMaxAmount ?? '';
  const gracePeriodDays = inputs.gracePeriodDays ?? '';
  const paymentDueDay = inputs.paymentDueDay ?? '';

  const rules = jurisdictionInfo?.rules as JurisdictionRules | undefined;

  // Security deposit constraint
  const maxMultiplier = rules?.security_deposit?.max_multiplier ?? null;
  const computedMaxDeposit =
    maxMultiplier != null && typeof rentAmount === 'number' && rentAmount > 0
      ? Math.floor(rentAmount * maxMultiplier)
      : null;

  // Late fee constraints from jurisdiction
  const allowedTypes = rules?.late_fees?.allowed_types ?? ['flat', 'percentage', 'daily'];
  const availableTypes: LateFeeType[] = ['none', ...allowedTypes];
  const maxFlatAmount = rules?.late_fees?.max_flat_amount ?? null;
  const maxPercentage = rules?.late_fees?.max_percentage ?? null;
  const maxDailyAmount = rules?.late_fees?.max_daily_amount ?? null;
  const maxDailyCap = rules?.late_fees?.max_daily_cap ?? null;

  const percentageBasis = rules?.late_fees?.percentage_basis ?? 'monthly_rent';

  // Grace period constraint
  const minGracePeriod = rules?.late_fees?.grace_period_days ?? null;

  // Set grace period and payment due day defaults once on mount
  useEffect(() => {
    const defaults: Partial<WizardState['inputs']> = {};
    if (inputs.gracePeriodDays == null) {
      defaults.gracePeriodDays = minGracePeriod ?? 5;
    }
    if (inputs.paymentDueDay == null) {
      defaults.paymentDueDay = 1;
    }
    if (Object.keys(defaults).length > 0) {
      updateInputs(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputClass =
    'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';
  const selectClass =
    'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
  const sectionClass = 'border border-border rounded-lg p-4';

  /** Find which tier a utility is currently in */
  const getUtilityTier = (utilityId: string): UtilityTier => {
    if (utilities.includedInRent.includes(utilityId)) return 'includedInRent';
    if (utilities.chargedBack.includes(utilityId)) return 'chargedBack';
    return 'tenantArranged';
  };

  /** Move a utility to a different tier */
  const setUtilityTier = (utilityId: string, newTier: UtilityTier) => {
    const updated: WizardState['utilities'] = {
      tenantArranged: utilities.tenantArranged.filter(u => u !== utilityId),
      includedInRent: utilities.includedInRent.filter(u => u !== utilityId),
      chargedBack: utilities.chargedBack.filter(u => u !== utilityId),
    };
    updated[newTier] = [...updated[newTier], utilityId];
    updateUtilities(updated);
  };

  function handleLateFeeTypeChange(newType: LateFeeType) {
    updateInputs({
      lateFeeType: newType,
      lateFeeAmount: newType === 'none' ? 0 : undefined,
      lateFeeMaxAmount: newType === 'daily' ? inputs.lateFeeMaxAmount : undefined,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Financial Terms</h3>
        <p className="text-sm text-muted-foreground">Set rent amount, deposit, and fee structure.</p>
      </div>

      {/* Rent Amount */}
      <div>
        <label htmlFor="rent-amount" className={labelClass}>Monthly Rent ($) <RequiredMark /></label>
        <input
          id="rent-amount"
          type="number"
          data-testid="rent-amount"
          value={rentAmount}
          min={1}
          step={1}
          onChange={e => updateInputs({ rentAmount: e.target.value ? Math.round(Number(e.target.value)) : undefined })}
          className={inputClass}
          placeholder="e.g. 2000"
          required
        />
      </div>

      {/* Security Deposit */}
      <div>
        <label htmlFor="security-deposit" className={labelClass}>Security Deposit ($) <RequiredMark /></label>
        <input
          id="security-deposit"
          type="number"
          data-testid="security-deposit"
          value={securityDeposit}
          min={0}
          step={1}
          max={computedMaxDeposit ?? undefined}
          onChange={e => updateInputs({ securityDeposit: e.target.value !== '' ? Math.round(Number(e.target.value)) : undefined })}
          className={inputClass}
          placeholder="e.g. 4000"
          required
        />
        {maxMultiplier != null && computedMaxDeposit != null && (
          <p
            data-testid="security-deposit-constraint"
            className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2"
          >
            {jurisdictionInfo?.displayName} limits security deposit to {maxMultiplier}x monthly rent
            = ${computedMaxDeposit.toLocaleString()}.
          </p>
        )}
        {maxMultiplier != null && computedMaxDeposit == null && (
          <p
            data-testid="security-deposit-constraint"
            className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2"
          >
            {jurisdictionInfo?.displayName} limits security deposit to {maxMultiplier}x monthly rent.
            Enter rent amount first to see the maximum.
          </p>
        )}
        <LocalRuleNote coverage={jurisdictionInfo?.coverage} category="security_deposit" />
      </div>

      {/* Late Fee Type */}
      <div>
        <label htmlFor="late-fee-type" className={labelClass}>Late Fee Type</label>
        <select
          id="late-fee-type"
          data-testid="late-fee-type"
          value={lateFeeType}
          onChange={e => handleLateFeeTypeChange(e.target.value as LateFeeType)}
          className={selectClass}
        >
          {availableTypes.map(type => (
            <option key={type} value={type}>{LATE_FEE_LABELS[type]}</option>
          ))}
        </select>
        {allowedTypes.length < 3 && (
          <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
            {jurisdictionInfo?.displayName} only allows: {allowedTypes.map(t => LATE_FEE_LABELS[t]).join(', ')}.
          </p>
        )}
      </div>

      {/* Late Fee Amount — shown for flat, percentage, daily */}
      {lateFeeType === 'flat' && (
        <div>
          <label htmlFor="late-fee-amount" className={labelClass}>Late Fee Amount ($)</label>
          <input
            id="late-fee-amount"
            type="number"
            data-testid="late-fee-amount"
            value={lateFeeAmount}
            min={0}
            step={1}
            max={maxFlatAmount ?? undefined}
            onChange={e => updateInputs({ lateFeeAmount: e.target.value !== '' ? Math.round(Number(e.target.value)) : undefined })}
            className={inputClass}
            placeholder="e.g. 50"
          />
          {maxFlatAmount != null && (
            <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
              Maximum ${maxFlatAmount} per {jurisdictionInfo?.displayName}.
            </p>
          )}
        </div>
      )}

      {lateFeeType === 'percentage' && (
        <div>
          <label htmlFor="late-fee-amount" className={labelClass}>Late Fee Percentage (%)</label>
          <input
            id="late-fee-amount"
            type="number"
            data-testid="late-fee-amount"
            value={lateFeeAmount}
            min={0}
            step={0.5}
            max={maxPercentage ?? undefined}
            onChange={e => updateInputs({ lateFeeAmount: e.target.value !== '' ? Number(e.target.value) : undefined })}
            className={inputClass}
            placeholder="e.g. 5"
          />
          {maxPercentage != null && (
            <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
              Maximum {maxPercentage}% per {jurisdictionInfo?.displayName}.
            </p>
          )}
          <p
            data-testid="late-fee-percentage-basis"
            className="mt-1 text-xs text-muted-foreground"
          >
            {percentageBasis === 'past_due_rent'
              ? `This percentage is calculated on the unpaid/past-due rent amount, as required by ${jurisdictionInfo?.displayName ?? 'your jurisdiction'}.`
              : 'This percentage is calculated on the monthly rent amount.'}
          </p>
        </div>
      )}

      {lateFeeType === 'daily' && (
        <>
          <div>
            <label htmlFor="late-fee-amount" className={labelClass}>Daily Late Fee ($/day)</label>
            <input
              id="late-fee-amount"
              type="number"
              data-testid="late-fee-amount"
              value={lateFeeAmount}
              min={0}
              step={1}
              max={maxDailyAmount ?? undefined}
              onChange={e => updateInputs({ lateFeeAmount: e.target.value !== '' ? Number(e.target.value) : undefined })}
              className={inputClass}
              placeholder="e.g. 10"
            />
            {maxDailyAmount != null && (
              <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
                Maximum ${maxDailyAmount}/day per {jurisdictionInfo?.displayName}.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="late-fee-max" className={labelClass}>Maximum Late Fee Cap ($) <RequiredMark /></label>
            <input
              id="late-fee-max"
              type="number"
              data-testid="late-fee-max"
              value={lateFeeMaxAmount}
              min={0}
              step={1}
              max={maxDailyCap ?? undefined}
              onChange={e => updateInputs({ lateFeeMaxAmount: e.target.value !== '' ? Math.round(Number(e.target.value)) : undefined })}
              className={inputClass}
              placeholder="e.g. 150"
              required
            />
            {maxDailyCap != null && (
              <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
                Maximum cap ${maxDailyCap} per {jurisdictionInfo?.displayName}.
              </p>
            )}
          </div>
        </>
      )}

      {lateFeeType !== 'none' && (
        <LocalRuleNote coverage={jurisdictionInfo?.coverage} category="late_fees" />
      )}

      {/* Grace Period */}
      <div>
        <label htmlFor="grace-period-days" className={labelClass}>Grace Period (days)</label>
        <input
          id="grace-period-days"
          type="number"
          data-testid="grace-period-input"
          value={gracePeriodDays}
          min={minGracePeriod ?? 0}
          step={1}
          onChange={e => updateInputs({ gracePeriodDays: e.target.value !== '' ? Number(e.target.value) : undefined })}
          className={inputClass}
          placeholder="e.g. 5"
        />
        {minGracePeriod != null && (
          <p className="mt-1 text-xs text-muted-foreground">
            Minimum {minGracePeriod} day{minGracePeriod !== 1 ? 's' : ''} required by{' '}
            {jurisdictionInfo?.displayName}.
          </p>
        )}
      </div>

      {/* Payment Due Day */}
      <div>
        <label htmlFor="payment-due-day" className={labelClass}>Payment Due Day (1-28) <RequiredMark /></label>
        <input
          id="payment-due-day"
          type="number"
          data-testid="payment-due-day"
          value={paymentDueDay}
          min={1}
          max={28}
          step={1}
          onChange={e => updateInputs({ paymentDueDay: e.target.value ? Number(e.target.value) : undefined })}
          className={inputClass}
          placeholder="e.g. 1"
          required
        />
        <p className="mt-1 text-xs text-muted-foreground">Day of the month rent is due (1-28).</p>
      </div>

      {/* --- Additional Financial Items --- */}
      <div className="border-t border-border pt-6 mt-2">
        <h3 className="text-base font-semibold text-foreground mb-1">Additional Charges</h3>
        <p className="text-sm text-muted-foreground">Parking fees and utility responsibility.</p>
      </div>

      {/* Parking */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-foreground">Parking</div>
            <div className="text-xs text-muted-foreground">Is parking included with this unit?</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={parking.included}
            aria-label="Parking included"
            data-testid="parking-toggle"
            onClick={() => updateParking({
              included: !parking.included,
              ...(!parking.included ? {} : { spaceNumber: undefined, monthlyFee: undefined }),
            })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              parking.included ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ${
              parking.included ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {parking.included && (
          <div className="flex flex-col gap-3 pt-3 border-t border-border">
            <div>
              <label htmlFor="parking-space" className="block mb-1 text-xs font-medium text-foreground">
                Parking Space Number or Description
              </label>
              <input
                id="parking-space"
                type="text"
                value={parking.spaceNumber ?? ''}
                onChange={e => updateParking({ spaceNumber: e.target.value || undefined })}
                className={inputClass}
                placeholder='e.g. "Space #12" or "Driveway"'
              />
            </div>
            <div>
              <label htmlFor="parking-fee" className="block mb-1 text-xs font-medium text-foreground">
                Monthly Parking Fee ($)
              </label>
              <input
                id="parking-fee"
                type="number"
                value={parking.monthlyFee ?? ''}
                min={0}
                onChange={e => updateParking({ monthlyFee: e.target.value ? Number(e.target.value) : undefined })}
                className={inputClass}
                placeholder="0 = included in rent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Utilities — Three-Tier Model */}
      <div className={sectionClass}>
        <div className="mb-3">
          <div className="text-sm font-semibold text-foreground">Utilities</div>
          <div className="text-xs text-muted-foreground">For each utility, select who is responsible.</div>
        </div>
        <div className="flex flex-col gap-3">
          {ALL_UTILITIES.map(utility => {
            const currentTier = getUtilityTier(utility.id);
            return (
              <div key={utility.id} className="flex items-center justify-between gap-3 py-1">
                <span className="text-sm text-foreground min-w-[120px]">{utility.label}</span>
                <select
                  data-testid={`utility-tier-${utility.id.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  value={currentTier}
                  onChange={e => setUtilityTier(utility.id, e.target.value as UtilityTier)}
                  className="text-sm border border-input rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {(Object.entries(TIER_LABELS) as [UtilityTier, string][]).map(([tier, label]) => (
                    <option key={tier} value={tier}>{label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
